import { getDisplayLocale } from "@/lib/format";
import { interpolate, type MsgVars } from "@shared/i18n";

/** A translator; the hook's `t` (useTranslation) satisfies it. English when omitted. */
export type RelativeTimeTranslate = (key: string, en: string, vars?: MsgVars) => string;
const english: RelativeTimeTranslate = (_key, en, vars) => interpolate(en, vars);

/**
 * "2m", "3h", "Yesterday", "Sep 4" — the short relative stamp a notification
 * row carries at its trailing edge, and the full date for its tooltip. The
 * same shape PropPay's notification centre uses, so the two apps read alike.
 */
export function relativeTime(
  iso: string | Date,
  now: Date = new Date(),
  t: RelativeTimeTranslate = english,
): { short: string; full: string } {
  const d = iso instanceof Date ? iso : new Date(iso);
  const full = d.toLocaleString(getDisplayLocale(), { dateStyle: "medium", timeStyle: "short" });
  const ms = now.getTime() - d.getTime();
  if (!Number.isFinite(ms)) return { short: "", full };
  const s = Math.max(0, Math.round(ms / 1000));
  if (s < 60) return { short: t("shellTimeNow", "now"), full };
  const m = Math.round(s / 60);
  if (m < 60) return { short: t("shellTimeMinutes", "{count}m", { count: m }), full };
  const h = Math.round(m / 60);
  if (h < 24) return { short: t("shellTimeHours", "{count}h", { count: h }), full };
  const days = Math.round(h / 24);
  if (days === 1) return { short: t("shellTimeYesterday", "Yesterday"), full };
  if (days < 7) return { short: t("shellTimeDays", "{count}d", { count: days }), full };
  const sameYear = d.getFullYear() === now.getFullYear();
  return { short: d.toLocaleDateString(getDisplayLocale(), sameYear ? { month: "short", day: "numeric" } : { month: "short", day: "numeric", year: "numeric" }), full };
}
